import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import ExportButtons from './ExportButtons';
import DateFilter, { DateInput, formatDisplayDate, matchesDateFilter } from './DateFilter';
import GlobalFilters from './GlobalFilters';
import { defaultGlobalFilters, applyGlobalFilters } from '../utils/filterUtils';
import CandidateKPIDetailsGrid from './CandidateKPIDetailsGrid';

const API = process.env.REACT_APP_BACKEND_API_URL;

// The exact 17-value Interview Stage list. Some entries share an "- Interview"
// suffix even though they mean opposite things (e.g. Prescreen Select vs
// Prescreen Reject both say "- Interview"), so bucket assignment below is
// semantic (by keyword), not a literal suffix match.
const STAGE_REMARK_OPTIONS = [
    'Awaiting Prescreen Slot - Interview Awaiting',
    'Prescreen Scheduled - Interview',
    'Prescreen Reject - Interview',
    'Glider Shared - Interview',
    'Glider Reject - Interview',
    'Awaiting Tech Round Slot - Interview Awaiting',
    'Tech Round Scheduled - Interview',
    'Tech Interview Reject - Interview',
    'Tech Interview Feedback - Pending',
    'Awaiting Client Round Slot - Interview Awaiting',
    'Client Round Select - Interview',
    'Client Round scheduled - Interview',
    'Client Round Reject - Interview',
    'Client Screen Reject - Interview',
    'Client Round Feedback - Pending',
    'Selected for Role - Client Onboarded Pending',
    'Onboarded Joined Company - Onboarded',
    'Evanti Screen Reject - Interview',
    'Candidate Back out',
    'Role Closed - Hold',
    'Role Hold - Hold',
    'Duplicate Profile',
];

// Item #4: the Call Remark dropdown. Item #1: these three also auto-reassign
// the task on the backend the moment they're set (kept in sync here by a refetch).
const CALL_REMARK_OPTIONS = ['Interested', 'Not Interested', 'Not Eligible', 'Not Picked Up', 'Not Reachable', 'Call Later', '60 Days Notice', '90 Days Notice', "Number Doesn't Exist",];
// const AUTO_REASSIGN_REMARKS = ['Not Picked Up', 'Not Reachable', 'Call Later'];

// Anything containing "Reject" or "Back out" is a terminal negative outcome —
// stays in the Candidates tab, counted in the Rejected stat.
const REJECTED_STAGE_VALUES = [
    'Prescreen Reject - Interview', 'Glider Reject - Interview', 'Tech Interview Reject - Interview',
    'Client Round Reject - Interview', 'Client Screen Reject - Interview', 'Evanti Screen Reject - Interview', 'Candidate Back out', 'Duplicate Profile',
];
// Anything containing "Onboarded" goes to the Onboarded tab.
const ONBOARDED_STAGE_VALUES = ['Selected for Role - Client Onboarded Pending', 'Onboarded Joined Company - Onboarded'];
// Exactly these two remarks form the HOLD / CLOSED group (shown on the Interviews page's
// HOLD / CLOSED tab). They are not active interviews, and they stay out of the Candidates tab.
const HOLD_STAGE_VALUES = ['Role Closed - Hold', 'Role Hold - Hold'];
// Everything else — Awaiting, Select, and Feedback (still-unresolved) states
// — is an active interview-stage candidate.
const INTERVIEW_STAGE_VALUES = STAGE_REMARK_OPTIONS.filter(
    v => !REJECTED_STAGE_VALUES.includes(v) && !ONBOARDED_STAGE_VALUES.includes(v) && !HOLD_STAGE_VALUES.includes(v)
);

const getCandidateBucket = (stageRemark) => {
    if (ONBOARDED_STAGE_VALUES.includes(stageRemark)) return 'onboarded';
    if (HOLD_STAGE_VALUES.includes(stageRemark)) return 'hold';
    if (INTERVIEW_STAGE_VALUES.includes(stageRemark)) return 'interviews';
    return 'candidates';
};

const selectStyle = { padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', minWidth: '150px' };
const smallSelectStyle = { ...selectStyle, minWidth: '140px', padding: '5px 7px', fontSize: '11px' };

const RecruiterCandidateDetails = ({ user, onLogout }) => {
    const navigate = useNavigate();

    const [kpiDrafts, setKpiDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingStageId, setSavingStageId] = useState(null);

    const [candidateSubTab, setCandidateSubTab] = useState('candidates');
    const [candFilterRemark, setCandFilterRemark] = useState('');
    const [candFilterJobRole, setCandFilterJobRole] = useState('');
    const [candFilterVendorName, setCandFilterVendorName] = useState('');
    const [candFilterEvantiSpoc, setCandFilterEvantiSpoc] = useState('');

    // Global filters — Company Name, Company SPOC, Received Date, Action Date. They scope the
    // data BEFORE it is split into the four tabs, so they apply to all of them (incl. KPI Details).
    const [globalFilters, setGlobalFilters] = useState(() => defaultGlobalFilters('all'));
    // Interview Date (Interviews tab) — the same DateFilter used everywhere else.
    const [interviewDateFilter, setInterviewDateFilter] = useState({ mode: 'All' });
    const [resetSignal, setResetSignal] = useState(0);

    const fetchInterviews = useCallback(() => {
        if (!user?.name) return;
        setLoading(true);
        fetch(`${API}/api/analytics/recruiter?recruiter_name=${encodeURIComponent(user.name)}`)
            .then(res => res.json())
            .then(data => setKpiDrafts(data.kpiDrafts || []))
            .catch(err => console.error('Failed to fetch candidates:', err))
            .finally(() => setLoading(false));
    }, [user]);

    useEffect(() => { fetchInterviews(); }, [fetchInterviews]);

    const scopedDrafts = useMemo(() => applyGlobalFilters(kpiDrafts, globalFilters), [kpiDrafts, globalFilters]);

    const candidatesByBucket = useMemo(() => {
        const buckets = { candidates: [], interviews: [], onboarded: [], hold: [] };
        scopedDrafts.forEach(k => { buckets[getCandidateBucket(k.stage_remark)].push(k); });
        return buckets;
    }, [scopedDrafts]);

    const rejectedCount = useMemo(
        () => scopedDrafts.filter(k => REJECTED_STAGE_VALUES.includes(k.stage_remark)).length,
        [scopedDrafts]
    );

    const jobRoleOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.job_role).filter(Boolean))).sort(), [kpiDrafts]);
    const vendorNameOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.vendor_name).filter(Boolean))).sort(), [kpiDrafts]);
    const etSpocOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.evanti_spoc).filter(Boolean))).sort(), [kpiDrafts]);

    const filteredCandidates = useMemo(() => {
        return (candidatesByBucket[candidateSubTab] || []).filter(k => {
            if (candFilterRemark && k.stage_remark !== candFilterRemark) return false;
            if (candFilterJobRole && k.job_role !== candFilterJobRole) return false;
            if (candFilterVendorName && k.vendor_name !== candFilterVendorName) return false;
            if (candFilterEvantiSpoc && k.evanti_spoc !== candFilterEvantiSpoc) return false;
            if (candidateSubTab === 'interviews' && !matchesDateFilter(k, interviewDateFilter, { field: 'interview_date', includeBlank: true })) return false;
            return true;
        });
    }, [candidatesByBucket, candidateSubTab, candFilterRemark, candFilterJobRole, candFilterVendorName, candFilterEvantiSpoc, interviewDateFilter]);

    const hasActiveFilters = candFilterRemark || candFilterJobRole || candFilterVendorName || candFilterEvantiSpoc || (candidateSubTab === 'interviews' && interviewDateFilter.mode !== 'All');

    const clearFilters = () => {
        setCandFilterRemark(''); setCandFilterJobRole('');
        setCandFilterVendorName(''); setCandFilterEvantiSpoc('');
        setInterviewDateFilter({ mode: 'All' }); setResetSignal(x => x + 1);
    };

    // The server enforces candidate ownership; surface its message when it rejects a change.
    const notifyFailure = async (response) => {
        try { const d = await response.json(); if (d && d.error) alert(d.error); } catch (e) { /* no body */ }
    };

    const handleCallRemarkChange = async (candidateId, value) => {
        setSavingStageId(candidateId);
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, remark: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/kpi-fields`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ remark: value, requester_name: user?.name, requester_role: user?.role })
            });
            // A trigger remark may have auto-reassigned the task (item #1) — refetch so
            // that change (and the reassignment badge) shows up immediately.
            if (!response.ok) {
                await notifyFailure(response);
                fetchInterviews();
            } else {
                // Remark changed -> server stamped Updated Date; mirror it locally.
                const saved = await response.json();
                setKpiDrafts(prev => prev.map(k => k.id === candidateId
                    ? { ...k, updated_date: saved.updated_date, remark_updated_at: saved.remark_updated_at } : k));
            }
        } catch (err) {
            console.error('Failed to save call remark:', err);
            fetchInterviews();
        } finally {
            setSavingStageId(null);
        }
    };

    // Candidates tab: Mobile Number edit. A real change stamps the candidate's
    // Updated Date server-side (PATCH .../call-update); invalid/failed saves
    // revert the field.
    const handleNumberChange = async (candidateId, inputEl) => {
        const value = (inputEl.value || '').trim();
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        if (value === (current.number || '')) return;
        setSavingStageId(candidateId);
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/call-update`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ number: value, requester_name: user?.name, requester_role: user?.role })
            });
            const data = await response.json();
            if (!response.ok) {
                alert(data.error || 'Failed to update the number.');
                inputEl.value = current.number || '';
                return;
            }
            setKpiDrafts(prev => prev.map(k => k.id === candidateId
                ? { ...k, number: data.number, updated_date: data.updated_date, remark_updated_at: data.remark_updated_at }
                : k));
        } catch (err) {
            console.error('Failed to save number:', err);
            inputEl.value = current.number || '';
        } finally {
            setSavingStageId(null);
        }
    };

    const handleStageRemarkChange = async (candidateId, value) => {
        setSavingStageId(candidateId);
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, stage_remark: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stage_remark: value })
            });
            if (!response.ok) { await notifyFailure(response); fetchInterviews(); }
        } catch (err) {
            console.error('Failed to save stage remark:', err);
            fetchInterviews();
        } finally {
            setSavingStageId(null);
        }
    };

    const handleInterviewDateTimeChange = async (candidateId, field, value) => {
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, [field]: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    interview_date: field === 'interview_date' ? value : current.interview_date,
                    interview_time: field === 'interview_time' ? value : current.interview_time
                })
            });
            if (!response.ok) { await notifyFailure(response); fetchInterviews(); }
        } catch (err) {
            console.error('Failed to save interview date/time:', err);
            fetchInterviews();
        }
    };

    const candidateExportColumns = [
        { key: 'candidate_code', label: 'Candidate ID' }, { key: 'entry_date', label: 'Date' },
        { key: 'task_display_id', label: 'Task ID' }, { key: 'job_role', label: 'Job Role' }, { key: 'req_id', label: 'Req ID' },
        { key: 'company', label: 'Company' },
        { key: 'name', label: 'Name' }, { key: 'number', label: 'Number' },
        { key: 'remark', label: 'Call Remark' }, { key: 'skill', label: 'Skill' }, { key: 'email', label: 'Email' },
        { key: 'location', label: 'Location' }, { key: 'current_employer', label: 'Employer' },
        { key: 'designation', label: 'Designation' }, { key: 'total_experience_years', label: 'Exp (Yrs)' },
        { key: 'relevant_experience_years', label: 'Relevant Exp (Yrs)' },
        { key: 'notice_period', label: 'Notice Period' }, { key: 'education', label: 'Education' },
        { key: 'vendor_name', label: 'Vendor Name' }, { key: 'client_spoc', label: 'Client SPOC' },
        { key: 'evanti_spoc', label: 'Evanti SPOC' }, { key: 'bill_rate', label: 'Evanti Pay / Bill Rate' },
        { key: 'current_package', label: 'Current Pkg' }, { key: 'expected_package', label: 'Expected Pkg' },
        { key: 'candidate_budget', label: 'Candidate Budget' }, { key: 'client_budget', label: 'Client Budget' },
        { key: 'details_remarks', label: 'Remarks' }, { key: 'stage_remark', label: 'Remark' },
        { key: 'interview_date', label: 'Interview Date' }, { key: 'interview_time', label: 'Interview Time' }
    ];

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Candidate Details" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/recruiter')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Candidate Details</h2>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Loading...</div>
            ) : (
                <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px', overflowX: 'auto' }}>
                    <GlobalFilters filters={globalFilters} onChange={setGlobalFilters} rows={kpiDrafts} dateDefault="all" show={{ company: true, spoc: true, dates: false }} />

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        {[
                            { key: 'candidates', label: `Candidates (${candidatesByBucket.candidates.length})` },
                            { key: 'kpi', label: `Candidate KPI Details (${scopedDrafts.length})` }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setCandidateSubTab(tab.key)}
                                style={{
                                    padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer',
                                    background: candidateSubTab === tab.key ? '#2563eb' : '#f1f5f9',
                                    color: candidateSubTab === tab.key ? '#fff' : '#475569'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {candidateSubTab === 'kpi' ? (
                        <CandidateKPIDetailsGrid
                            kpiDrafts={scopedDrafts}
                            currentUser={user}
                            onRowSaved={(candidateId, updatedFields) => {
                                setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, ...updatedFields } : k));
                            }}
                        />
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                                        {filteredCandidates.length} candidate{filteredCandidates.length === 1 ? '' : 's'}
                                    </h3>
                                    <ExportButtons
                                        rows={filteredCandidates}
                                        columns={candidateExportColumns}
                                        filename={`my-candidates-${candidateSubTab}`}
                                        title="My Candidates Report"
                                    />
                                    {candidateSubTab === 'candidates' && (
                                        <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: '700' }}>
                                            <span style={{ color: '#059669' }}>Interviews: {candidatesByBucket.interviews.length}</span>
                                            <span style={{ color: '#dc2626' }}>Rejected: {rejectedCount}</span>
                                            <span style={{ color: '#2563eb' }}>Onboarded: {candidatesByBucket.onboarded.length}</span>
                                            <span style={{ color: '#d97706' }}>Hold / Closed: {candidatesByBucket.hold.length}</span>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <select style={smallSelectStyle} value={candFilterJobRole} onChange={(e) => setCandFilterJobRole(e.target.value)}>
                                        <option value="">All Job Roles</option>
                                        {jobRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    {/* <select style={smallSelectStyle} value={candFilterVendorName} onChange={(e) => { setCandFilterVendorName(e.target.value); }}>
                                        <option value="">All Vendor Names</option>
                                        {vendorNameOptions.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select> */}
                                    <select style={smallSelectStyle} value={candFilterEvantiSpoc} onChange={(e) => setCandFilterEvantiSpoc(e.target.value)}>
                                        <option value="">All Evanti SPOCs</option>
                                        {etSpocOptions.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                    <select style={smallSelectStyle} value={candFilterRemark} onChange={(e) => setCandFilterRemark(e.target.value)}>
                                        <option value="">All Remarks</option>
                                        {STAGE_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>

                                    {hasActiveFilters && (
                                        <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                                            <X size={12} /> Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {candidateSubTab === 'interviews' && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>Interview Date</div>
                                    <DateFilter key={`interview-${resetSignal}`} dateField="interview_date" includeBlank initialValue={interviewDateFilter} onFilterChange={setInterviewDateFilter} />
                                </div>
                            )}

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                                        {['Recruiter', 'Current Recruiter', 'Company', 'Job Role', 'Name', 'Interview Date', 'Interview Time', 'Remark',
                                            'Evanti SPOC', 'Client SPOC', 'Candidate ID', 'Date', 'Number', 'Call Remark', 'Remarks',
                                            'Skill', 'Email', 'Location', 'Employer', 'Designation', 'Exp (Yrs)', 'Relevant Exp (Yrs)',
                                            'Notice Period', 'Education', 'Evanti Pay / Bill Rate', 'Current Pkg', 'Expected Pkg',
                                            'Candidate Budget', 'Client Budget'
                                        ].map(h => (
                                            <th key={h} style={{
                                                padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap',
                                                ...(h === 'Recruiter' ? { width: '80px', minWidth: '80px', maxWidth: '80px' } : {}),
                                                ...(h === 'Current Recruiter' ? { width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' } : {}),
                                                ...(h === 'Job Role' ? { width: '150px', minWidth: '150px', maxWidth: '150px' } : {}),
                                                ...(h === 'Name' ? { width: '90px', minWidth: '90px', maxWidth: '90px' } : {}),
                                                ...(h === 'Interview Date' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                                ...(h === 'Interview Time' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                                ...(h === 'Remark' ? { width: '230px', minWidth: '230px', maxWidth: '230px' } : {}),
                                                ...(h === 'Candidate ID' ? { width: '80px', minWidth: '80px', maxWidth: '80px' } : {}),
                                                ...(h === 'Remarks' ? { width: '330px', minWidth: '330px', maxWidth: '330px' } : {}),
                                            }}
                                            >{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCandidates.map((k, snoIdx) => (
                                        <tr key={k.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px', color: '#64748b', fontWeight: '600' }}>{snoIdx + 1}</td>
                                            <td style={{ padding: '8px', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.assigned_to || '-'}</td>
                                            <td style={{ padding: '8px', color: '#2563eb', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.task_assigned_to}</td>
                                            <td style={{ padding: '8px' }}>{k.company || '-'}</td>
                                            <td style={{ padding: '8px', width: '150px', minWidth: '150px', maxWidth: '150px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.job_role || 'N/A'}</td>
                                            <td style={{ padding: '8px', width: '90px', minWidth: '90px', maxWidth: '90px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.name || '-'}</td>
                                            <td style={{ padding: '8px' }}>
                                                <DateInput style={{ ...smallSelectStyle, width: '100px', minWidth: '100px', maxWidth: '100px' }} value={k.interview_date || ''} onChange={(e) => handleInterviewDateTimeChange(k.id, 'interview_date', e.target.value)} />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input type="time" style={{ ...smallSelectStyle, width: '100px', minWidth: '100px', maxWidth: '100px' }} value={k.interview_time || ''} onChange={(e) => handleInterviewDateTimeChange(k.id, 'interview_time', e.target.value)} />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <select
                                                    style={{ ...smallSelectStyle, width: '230px', minWidth: '230px', maxWidth: '230px', }}
                                                    value={k.stage_remark || ''}
                                                    disabled={savingStageId === k.id}
                                                    onChange={(e) => handleStageRemarkChange(k.id, e.target.value)}
                                                >
                                                    <option value="">-</option>
                                                    {STAGE_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ padding: '8px' }}>{k.evanti_spoc || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.client_spoc || '-'}</td>
                                            <td style={{ padding: '8px', fontWeight: '700', color: '#7c3aed', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.candidate_code || '-'}</td>
                                            <td style={{ padding: '8px' }}>{formatDisplayDate(k.entry_date)}</td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    key={`${k.id}-${k.number || ''}`}
                                                    defaultValue={k.number || ''}
                                                    disabled={savingStageId === k.id}
                                                    onBlur={(e) => handleNumberChange(k.id, e.target)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                    aria-label="Mobile number"
                                                    style={{ width: '120px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <select
                                                    style={smallSelectStyle}
                                                    value={k.remark || ''}
                                                    disabled={savingStageId === k.id}
                                                    onChange={(e) => handleCallRemarkChange(k.id, e.target.value)}
                                                >
                                                    <option value="">-</option>
                                                    {CALL_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ padding: '8px', width: '330px', minWidth: '330px', maxWidth: '330px' }}>{k.details_remarks || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.skill || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.email || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.location || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.current_employer || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.designation || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.total_experience_years ?? '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.relevant_experience_years ?? '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.notice_period || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.education || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.bill_rate || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.current_package || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.expected_package || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.candidate_budget || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.client_budget || '-'}</td>
                                        </tr>
                                    ))}
                                    {filteredCandidates.length === 0 && (
                                        <tr><td colSpan={30} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No candidates found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default RecruiterCandidateDetails;